import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import {
  addGoals,
  applyActiveAssessmentPrefill,
  setActiveAssessmentPrefill,
  setGoalsPagination,
  totalAssessmentCount,
} from '../store/slices/GoalSlice';
import { ActiveAssessmentPrefill, AssessmentPagination } from '../types/goal';
import useApi from './useApi';
import API from '../services/rest/api';
import processGoalData from '../helpers/goal';
import { toast } from 'react-toastify';
import { ASSESSMENTS_PER_PAGE } from '../constants/goal';

export interface FetchGoalsParams {
  page?: number;
  per_page?: number;
}

export const useGoals = (
  activeTab: string,
  patientId: string,
  isPrimary: boolean,
  fetchParams?: FetchGoalsParams
) => {
  const dispatch = useDispatch();
  const fetchParamsRef = useRef(fetchParams);
  fetchParamsRef.current = fetchParams;

  const {
    mutate: fetchAssessmentGoals,
    response: assessmentGoalsResponse,
    error: assessmentGoalsError,
    isLoading: isAssessmentGoalsLoading
  } = useApi(API.getAssessmentGoals);

  useEffect(() => {
    if (assessmentGoalsResponse?.code?.toString() === "1") {
      const responseData = assessmentGoalsResponse?.data;
      const goalsArray = Array.isArray(responseData)
        ? responseData
        : responseData?.goals ?? [];

      const processedData = processGoalData(goalsArray, isPrimary);
      const goalType = isPrimary ? "mainGoal" : "customGoals";
      const goalBucket = processedData?.data?.[goalType];

      dispatch(addGoals({
        goals: goalBucket?.goals ?? {},
        assessments: goalBucket?.assessments ?? {},
        isPrimary: isPrimary || false
      }));

      const pagination: AssessmentPagination = responseData?.pagination ?? {
        page: fetchParamsRef.current?.page ?? 1,
        per_page: fetchParamsRef.current?.per_page ?? ASSESSMENTS_PER_PAGE,
        totalRecords: assessmentGoalsResponse?.total_data ?? 0,
        totalPages: Math.ceil(
          (assessmentGoalsResponse?.total_data ?? 0) /
            (fetchParamsRef.current?.per_page ?? ASSESSMENTS_PER_PAGE)
        ) || 0,
      };

      dispatch(setGoalsPagination({ pagination, isPrimary: isPrimary || false }));
      dispatch(totalAssessmentCount({
        count: assessmentGoalsResponse?.total_data ?? pagination.totalRecords,
        isPrimary: isPrimary || false
      }));

      const activeAssessmentPrefill: ActiveAssessmentPrefill | null =
        responseData?.activeAssessmentPrefill ?? null;
      dispatch(setActiveAssessmentPrefill({
        prefill: activeAssessmentPrefill,
        isPrimary: isPrimary || false,
      }));

      if (activeAssessmentPrefill) {
        dispatch(applyActiveAssessmentPrefill({ isPrimary: isPrimary || false }));
      }
    } else if (assessmentGoalsResponse?.code?.toString() === "0") {
      toast.error(assessmentGoalsResponse?.message || "Failed to fetch goals");
    }
  }, [assessmentGoalsResponse, assessmentGoalsError, dispatch, isPrimary]);

  const fetchGoals = useCallback(async (params?: FetchGoalsParams) => {
    const patientInfo = JSON.parse(localStorage.getItem("PatientInfo") || "{}");
    const page = params?.page ?? fetchParamsRef.current?.page ?? 1;
    const per_page = params?.per_page ?? fetchParamsRef.current?.per_page ?? ASSESSMENTS_PER_PAGE;

    const payload = {
      page,
      per_page,
      tag: activeTab,
      disease: patientInfo?.disease || "",
      patient_id: patientId,
      is_primary: isPrimary ? 1 : 0
    };

    try {
      fetchAssessmentGoals(payload);
    } catch (error) {
      console.error('Error fetching goals:', error);
    }
  }, [activeTab, patientId, isPrimary, fetchAssessmentGoals]);

  useEffect(() => {
    if (patientId && activeTab) {
      fetchGoals({
        page: fetchParams?.page ?? 1,
        per_page: fetchParams?.per_page ?? ASSESSMENTS_PER_PAGE,
      });
    }
    // Only refetch when tab/patient context changes — page changes are handled explicitly by AssessmentGoal
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, patientId, isPrimary]);

  return { isLoading: isAssessmentGoalsLoading, fetchGoals };
};
